import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { saveAs } from "file-saver";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { SupportSocketService } from "src/app/shared/services/support/support-socket.service";
import { SupportService } from "src/app/shared/services/support/support.service";
import { UserDetailsService } from "src/app/shared/services/user-details.service";
import { SupportChatFacade } from "./support-chat.facade";

@Component({
  selector: "app-support-widget",
  templateUrl: "./support-widget.component.html",
  styleUrls: ["./support-widget.component.scss"],
})
export class SupportWidgetComponent
  extends SupportChatFacade
  implements OnInit, OnDestroy
{
  private readonly supportAttachmentRetryAttempts = new WeakMap<HTMLImageElement, number>();
  isSupportImagePreviewOpen = false;
  supportImagePreviewUrl = "";
  supportImagePreviewName = "attachment";
  supportImagePreviewType: "image" | "video" = "image";
  hasAttendanceDock = false;
  isAttendanceRoute = false;
  isDiscussionRoute = false;
  isReportCustomizeDrawerOpen = false;
  private attendanceDockObserver: MutationObserver | null = null;
  private routerSubscription: Subscription | null = null;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router,
    userDetailsService: UserDetailsService,
    supportService: SupportService,
    toastrService: ToastrService,
    supportSocketService: SupportSocketService
  ) {
    super(
      userDetailsService,
      supportService,
      toastrService,
      supportSocketService
    );
  }

  async ngOnInit() {
    this.syncAttendanceRouteState();
    this.syncDiscussionRouteState();
    this.syncReportCustomizeDrawerState();
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncAttendanceRouteState();
        this.syncDiscussionRouteState();
        this.syncReportCustomizeDrawerState();
      }
    });
    this.watchAttendanceDock();
    await this.initializeSupportWidget();
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
    this.attendanceDockObserver?.disconnect();
    this.attendanceDockObserver = null;
    this.destroySupportWidget();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (clickedInside) {
      return;
    }

    this.closeSupportMessageMenu();
    if (this.isSupportShellOpen) {
      this.closeSupportShell();
    }
  }

  @HostListener("window:resize")
  onWindowResize() {
    this.syncAttendanceDockState();
  }

  onSupportMessagesScroll(event: Event) {
    const target = event.target as HTMLElement | null;
    if (!target || target.scrollTop > 24) {
      return;
    }

    void this.loadOlderSupportHistory(target);
  }

  openSupportImagePreview(attachment: any, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    const url = this.getSupportAttachmentUrl(attachment);
    if (!url) {
      return;
    }

    this.supportImagePreviewUrl = url;
    this.supportImagePreviewName =
      attachment?.attachement_name || attachment?.name || "attachment";
    this.supportImagePreviewType = this.isSupportVideoAttachment(attachment)
      ? "video"
      : "image";
    this.isSupportImagePreviewOpen = true;
  }

  onSupportAttachmentImageError(event: Event, attachment: any) {
    const target = event?.target as HTMLImageElement | null;
    if (!target) {
      return;
    }

    const previewSrc = String(this.getSupportAttachmentPreviewSrc(attachment) || "").trim();
    const fallbackSrc = this.getSupportAttachmentFallbackSrc(attachment);
    const currentSrc = String(target.getAttribute("src") || target.src || "").trim();

    if (currentSrc === fallbackSrc) {
      return;
    }

    const isRemotePreview =
      !!previewSrc &&
      !previewSrc.startsWith("assets/") &&
      previewSrc !== fallbackSrc;
    const retryAttempt = this.supportAttachmentRetryAttempts.get(target) || 0;

    if (isRemotePreview && retryAttempt < 2) {
      const retrySrc = `${previewSrc}${previewSrc.includes("?") ? "&" : "?"}retry=${Date.now()}`;
      this.supportAttachmentRetryAttempts.set(target, retryAttempt + 1);
      setTimeout(() => {
        target.src = retrySrc;
      }, 350);
      return;
    }

    this.supportAttachmentRetryAttempts.delete(target);
    target.src = fallbackSrc;
  }

  onSupportAttachmentImageLoad() {
    if (!this.isSupportShellOpen || this.currentSupportScreen !== "chat") {
      return;
    }

    this.scrollSupportChatToBottom();
  }

  async downloadSupportAttachment(attachment: any, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    const fileUrl = this.getSupportAttachmentUrl(attachment);
    const fileName =
      attachment?.attachement_name || attachment?.name || "attachment";

    if (!fileUrl) {
      return;
    }

    try {
      const blob = await this.supportService.downloadSupportAttachment(
        fileUrl,
        fileName
      );
      saveAs(blob, fileName);
    } catch (_error) {
      this.toastrService.error("Unable to download attachment right now.");
    }
  }

  getSupportAttachmentExtension(attachment: any) {
    const fileName = String(
      attachment?.attachement_name || attachment?.name || attachment?.file_name || ""
    );
    const parts = fileName.split(".");
    const extension = parts.length > 1 ? parts[parts.length - 1] : "";
    return extension ? extension.toUpperCase() : "FILE";
  }

  hasPendingSupportImagePreview(attachment: any) {
    return !!(
      attachment?.isImage &&
      attachment?.previewUrl &&
      !attachment?.previewFailed
    );
  }

  getPendingSupportPreviewSrc(attachment: any): SafeUrl | string {
    if (this.hasPendingSupportImagePreview(attachment)) {
      return this.sanitizer.bypassSecurityTrustUrl(
        String(attachment.previewUrl)
      );
    }

    return "assets/images/document-icon/default.png";
  }

  isPendingSupportVideoAttachment(attachment: any) {
    return !!attachment?.isVideo;
  }

  onPendingSupportImageError(index: number) {
    if (!this.pendingSupportAttachments[index]) {
      return;
    }

    this.pendingSupportAttachments[index] = {
      ...this.pendingSupportAttachments[index],
      previewFailed: true,
    };
    this.pendingSupportAttachments = [...this.pendingSupportAttachments];
  }

  closeSupportImagePreview(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.isSupportImagePreviewOpen = false;
    this.supportImagePreviewUrl = "";
    this.supportImagePreviewName = "attachment";
    this.supportImagePreviewType = "image";
  }

  async downloadSupportImagePreview(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.supportImagePreviewUrl) {
      return;
    }

    try {
      const blob = await this.supportService.downloadSupportAttachment(
        this.supportImagePreviewUrl,
        this.supportImagePreviewName
      );
      saveAs(blob, this.supportImagePreviewName || "attachment");
    } catch (_error) {
      this.toastrService.error("Unable to download attachment right now.");
    }
  }

  private watchAttendanceDock() {
    this.syncAttendanceDockState();
    this.syncReportCustomizeDrawerState();

    if (typeof MutationObserver === "undefined" || !document?.body) {
      return;
    }

    this.attendanceDockObserver = new MutationObserver(() => {
      this.syncAttendanceDockState();
      this.syncReportCustomizeDrawerState();
    });

    this.attendanceDockObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
  }

  private syncAttendanceDockState() {
    this.hasAttendanceDock = !!document.querySelector(".check-in.d-flex");
  }

  private syncDiscussionRouteState() {
    this.isDiscussionRoute = this.router.url.startsWith("/discussion");
  }

  private syncAttendanceRouteState() {
    this.isAttendanceRoute = this.router.url.includes("/attendance");
  }

  private syncReportCustomizeDrawerState() {
    const isReportRoute = this.router.url.includes("/report/");
    if (!isReportRoute) {
      this.isReportCustomizeDrawerOpen = false;
      return;
    }

    this.isReportCustomizeDrawerOpen = !!document.querySelector(
      ".mat-drawer.customize--drawer.report-drawer.mat-drawer-opened"
    );
  }

}
